/** Odontograma FDI (32 piezas) — persistencia en expediente (nota ODONTOGRAMA) + caché local. */

import { Notas, type Nota } from './api';

export type EstadoPieza =
  | 'sano'
  | 'caries'
  | 'restaurado'
  | 'corona'
  | 'endodoncia'
  | 'ausente'
  | 'extraccion'
  | 'implante';

export type CambioOdontograma = {
  pieza: number;
  estado: EstadoPieza;
  fecha: string;
};

export type OdontogramaPaciente = {
  pacienteId: string;
  piezas: Record<number, EstadoPieza>;
  historial: CambioOdontograma[];
};

export const ODONTOGRAMA_TIPO = 'ODONTOGRAMA';

/** FDI permanente: 18-11, 21-28, 48-41, 31-38 */
export const FDI_SUP_DER = [18, 17, 16, 15, 14, 13, 12, 11] as const;
export const FDI_SUP_IZQ = [21, 22, 23, 24, 25, 26, 27, 28] as const;
export const FDI_INF_DER = [48, 47, 46, 45, 44, 43, 42, 41] as const;
export const FDI_INF_IZQ = [31, 32, 33, 34, 35, 36, 37, 38] as const;
export const FDI_TODAS = [...FDI_SUP_DER, ...FDI_SUP_IZQ, ...FDI_INF_DER, ...FDI_INF_IZQ];

export const ESTADO_LABEL: Record<EstadoPieza, string> = {
  sano: 'Sano',
  caries: 'Caries',
  restaurado: 'Restauración',
  corona: 'Corona',
  endodoncia: 'Endodoncia',
  ausente: 'Ausente',
  extraccion: 'Extracción indicada',
  implante: 'Implante',
};

export const ESTADO_COLOR: Record<EstadoPieza, string> = {
  sano: '#ffffff',
  caries: '#ef4444',
  restaurado: '#3b82f6',
  corona: '#f59e0b',
  endodoncia: '#8b5cf6',
  ausente: '#94a3b8',
  extraccion: '#dc2626',
  implante: '#0f766e',
};

const STORAGE_KEY = 'toscana.expediente.odontograma';

function readAllLocal(): OdontogramaPaciente[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as OdontogramaPaciente[]) : [];
  } catch {
    return [];
  }
}

function writeAllLocal(items: OdontogramaPaciente[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function hoy(): string {
  return new Date().toISOString().slice(0, 10);
}

function vacio(pacienteId: string): OdontogramaPaciente {
  return { pacienteId, piezas: {}, historial: [] };
}

export function serializeOdontograma(o: OdontogramaPaciente): string {
  return JSON.stringify({ piezas: o.piezas, historial: o.historial });
}

export function parseOdontogramaNota(n: Nota, pacienteId: string): OdontogramaPaciente | null {
  if (n.tipo !== ODONTOGRAMA_TIPO) return null;
  try {
    const o = JSON.parse(n.contenido) as { piezas?: Record<number, EstadoPieza>; historial?: CambioOdontograma[] };
    if (!o?.piezas) return null;
    return {
      pacienteId,
      piezas: o.piezas,
      historial: o.historial ?? [],
    };
  } catch {
    return null;
  }
}

export function odontogramaDesdeNotas(notas: Nota[], pacienteId: string): OdontogramaPaciente | null {
  const candidatas = notas
    .filter(n => n.tipo === ODONTOGRAMA_TIPO)
    .map(n => parseOdontogramaNota(n, pacienteId))
    .filter((o): o is OdontogramaPaciente => o != null);
  return candidatas[0] ?? null;
}

export function getOdontogramaLocal(pacienteId: string): OdontogramaPaciente {
  const found = readAllLocal().find(o => o.pacienteId === pacienteId);
  return found ?? vacio(pacienteId);
}

/** @deprecated Use fetchOdontograma — mantiene compatibilidad con caché local. */
export function getOdontograma(pacienteId: string): OdontogramaPaciente {
  return getOdontogramaLocal(pacienteId);
}

function cacheLocal(o: OdontogramaPaciente) {
  const all = readAllLocal().filter(x => x.pacienteId !== o.pacienteId);
  all.push(o);
  writeAllLocal(all);
}

export async function fetchOdontograma(pacienteId: string): Promise<OdontogramaPaciente> {
  if (!pacienteId) return vacio('');
  try {
    const notas = await Notas.porPaciente(pacienteId);
    const remoto = odontogramaDesdeNotas(notas, pacienteId);
    if (remoto) {
      cacheLocal(remoto);
      return remoto;
    }
    const local = getOdontogramaLocal(pacienteId);
    if (local.piezas && Object.keys(local.piezas).length > 0) {
      await persistOdontograma(pacienteId, local);
      return local;
    }
    return vacio(pacienteId);
  } catch {
    return getOdontogramaLocal(pacienteId);
  }
}

export async function persistOdontograma(
  pacienteId: string,
  odonto: OdontogramaPaciente,
  autor = 'Odontólogo',
): Promise<OdontogramaPaciente> {
  const payload = { ...odonto, pacienteId };
  cacheLocal(payload);
  await Notas.crear({
    pacienteId,
    autor,
    tipo: ODONTOGRAMA_TIPO,
    contenido: serializeOdontograma(payload),
  });
  return payload;
}

export async function setPiezaEstadoAsync(
  pacienteId: string,
  pieza: number,
  estado: EstadoPieza,
  autor = 'Odontólogo',
): Promise<OdontogramaPaciente> {
  const base = await fetchOdontograma(pacienteId);
  const piezas = { ...base.piezas, [pieza]: estado };
  const historial = [{ pieza, estado, fecha: hoy() }, ...base.historial].slice(0, 20);
  return persistOdontograma(pacienteId, { pacienteId, piezas, historial }, autor);
}

/** Solo caché local (sin API). */
export function setPiezaEstado(
  pacienteId: string,
  pieza: number,
  estado: EstadoPieza,
): OdontogramaPaciente {
  const all = readAllLocal();
  let entry = all.find(o => o.pacienteId === pacienteId);
  if (!entry) {
    entry = vacio(pacienteId);
    all.push(entry);
  }
  entry.piezas[pieza] = estado;
  entry.historial = [{ pieza, estado, fecha: hoy() }, ...entry.historial].slice(0, 20);
  writeAllLocal(all);
  return entry;
}

export async function seedOdontogramaDemo(
  pacienteId: string,
  piezas: Record<number, EstadoPieza>,
  autor = 'Odontólogo Demo',
): Promise<void> {
  const historial = Object.entries(piezas)
    .filter(([, e]) => e !== 'sano')
    .map(([n, estado]) => ({ pieza: Number(n), estado, fecha: '2026-03-01' }));
  const odonto: OdontogramaPaciente = { pacienteId, piezas, historial };
  cacheLocal(odonto);
  try {
    const notas = await Notas.porPaciente(pacienteId);
    if (!odontogramaDesdeNotas(notas, pacienteId)) {
      await persistOdontograma(pacienteId, odonto, autor);
    }
  } catch {
    /* demo offline: caché local basta */
  }
}

export function contarPorEstado(piezas: Record<number, EstadoPieza>, estado: EstadoPieza): number {
  return Object.values(piezas).filter(e => e === estado).length;
}
